import { useLocalObservable } from 'mobx-react-lite';

import { CallBlurType } from '@gem/call/constants';
import { callBackground } from '@gem/call/storage/adapters';
import { platformInfo } from '@gem/utils';

import { BlurProcessor } from './BlurProcessor/BlurProcessor';

export const BlurOptions = {
  [CallBlurType.Small]: 25,
  [CallBlurType.Large]: 65,
  [CallBlurType.None]: 0,
};

export interface IRoomCallBackgroundVideoPresenter {
  blurType: CallBlurType;
  backgroundProcessor: BlurProcessor | null;
  backgroundVideoStream: MediaStream | null;
  canShowBackgroundPanel: boolean;
  destroyBackgroundProcessor(): void;
  addBackgroundToMediaTrack(mediaStream: MediaStream | null): Promise<void>;
  setBackground(blurType: CallBlurType, mediaStream: MediaStream | null): Promise<void>;
  dispose(): void;
}

export const useRoomCallBackgroundVideoPresenter = (): IRoomCallBackgroundVideoPresenter => {
  const presenter = useLocalObservable<IRoomCallBackgroundVideoPresenter>(() => ({
    backgroundProcessor:
      !platformInfo.isMobile && !platformInfo.isSafari ? new BlurProcessor() : null,
    backgroundVideoStream: null,
    blurType:
      platformInfo.isMobile || platformInfo.isSafari
        ? CallBlurType.None
        : callBackground.getBlurBackgroundType() || CallBlurType.None,

    get canShowBackgroundPanel(): boolean {
      return !platformInfo.isMobile && !platformInfo.isSafari;
    },

    async addBackgroundToMediaTrack(mediaStream: MediaStream | null): Promise<void> {
      if (presenter.blurType === CallBlurType.None) {
        return;
      }
      await presenter.setBackground(presenter.blurType, mediaStream);
    },

    async setBackground(blurType: CallBlurType, mediaStream: MediaStream | null): Promise<void> {
      if (!presenter.backgroundProcessor) {
        return;
      }

      if (presenter.blurType !== blurType) {
        presenter.blurType = blurType;
        callBackground.setBlurBackgroundType(blurType);
      }

      if (
        !mediaStream ||
        (blurType === CallBlurType.None && !presenter.backgroundProcessor.isStarted)
      ) {
        return;
      }

      if (blurType === CallBlurType.None) {
        presenter.backgroundProcessor.destroy();
        return;
      }

      const track = mediaStream.getVideoTracks()[0];

      presenter.backgroundVideoStream = await presenter.backgroundProcessor.init(mediaStream);

      const constraints = track.getSettings();
      presenter.backgroundProcessor.setTrackConstraints(
        constraints.width || 0,
        constraints.height || 0
      );

      presenter.backgroundProcessor.setBlurValue(BlurOptions[blurType]);
    },

    async dispose(): Promise<void> {
      if (presenter.backgroundProcessor) {
        presenter.backgroundProcessor.dispose();
      }
    },

    destroyBackgroundProcessor(): void {
      if (presenter.backgroundProcessor) {
        presenter.backgroundProcessor.destroy();
      }
    },
  }));

  return presenter;
};
